import React, { useState } from 'react';
import { useForm, useFieldArray, SubmitHandler } from 'react-hook-form';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SubmitButton from '../../../form/SubmitButton';
import InputField from '../../../form/InputField';
import { Plus, Trash2 } from 'lucide-react';
import SubTitle from '../../../ui/SubTitle';
import ImageUploader from '../ImageUploader';
import { HexColorPicker } from 'react-colorful';
import { useUpdateResource } from '../../../../hooks/useUpdateResource';
import LoadingButton from '../../../form/LoadingButton';

type Language = {
  languageCode: string;
  name: string;
};

type Variant = {
  additional_price: number;
  categoryId?: number;
  languages: Language[];
};

type FormValues = {
  productId: number;
  variants: Variant[];
};


type Props={
  categoriesList:{id:number,name:string}[]
}

export default function VariantsForm  ({categoriesList}:Props) {
  const { t } = useTranslation();
    const { productId ,storeId} = useParams<{ productId: string ,storeId:string}>();
    const numericStoreId = storeId ? parseInt(storeId) : undefined;
    const numericProductId = productId ? parseInt(productId) : 0;

  const [searchParams] = useSearchParams();
  const backQuery = (searchParams.get('back'));
  const {loading,updateResource} = useUpdateResource();
    const navigate = useNavigate();

  const { control, register, handleSubmit, formState: { errors }, setError, clearErrors, getValues, reset } = useForm<FormValues>({
    defaultValues: { productId: numericProductId, variants: [] }
  });

  const { fields, append, remove, update } = useFieldArray({
    control,
    name: 'variants'
  });

  const [images, setImages] = useState<{ [key: string]: File }>({});
  const [colorValues, setColorValues] = useState<{ [key: number]: string }>({});

  const selectCategory = (variantIndex: number, categoryId: number) => {
    const currentVariant = getValues(`variants.${variantIndex}`);
    update(variantIndex, { ...currentVariant, categoryId }); // useFieldArray update
    clearErrors(`variants.${variantIndex}.categoryId`);

    const selectedCat = categoriesList.find(cat => cat.id === categoryId);
    const name = selectedCat?.name?.trim();
    if (name === 'Color' || name==="اللون") {
      const currentColor = currentVariant.languages?.[0]?.name || '#000000';
      setColorValues(prev => ({ ...prev, [variantIndex]: currentColor }));
      reset({
        ...getValues(),
        variants: getValues('variants').map((v, i) =>
          i === variantIndex
            ? { ...v, languages: [{ languageCode: 'ar', name: currentColor }, { languageCode: 'en', name: currentColor }] }
            : v
        )
      });
    }
  };

  const onImageChange = (variantIndex: number, file: File) => {
    setImages(prev => ({ ...prev, [`image_${variantIndex}`]: file }));
  };

  const navigatedLink = numericStoreId ? `/admin/stores/${numericStoreId}/products` :`/store/products`

  const backLink = numericStoreId ? `/admin/stores/${numericStoreId}/products/${numericProductId}` :`/store/products/${numericProductId}`

  const onSubmit: SubmitHandler<FormValues> = (data) => {
    let hasError = false;
    data.variants.forEach((v, i) => {
      if (!v.categoryId) {
        setError(`variants.${i}.categoryId`, { type: 'required', message: t('required_field') });
        hasError = true;
      }
    });
    if (hasError) return;

    const formData = new FormData();
    formData.append('productId', String(numericProductId));
    const variants = data.variants.map(ex=>({additionalPrice:ex.additional_price,languages:ex.languages,categoryId:ex.categoryId}));;
    formData.append("variants", JSON.stringify(variants));
    data.variants.forEach((variant, i) => {
      if (images[`image_${i}`]) {
        formData.append(`image_${i}`, images[`image_${i}`]);
      }
    });

    const endPoint = numericStoreId?`product-variant?storeId=${numericStoreId}`:"product-variant"
    updateResource(endPoint,formData, numericStoreId? "admin" : "store",{
      method:"POST",
      successMessage:t('createdSuccess'),
      navigateAfter:()=>backQuery==="true"?navigate(-1) :navigate(navigatedLink)}
    )
  };

  return (
    <div className="max-w-[800px]">
      <SubTitle title="add_variants" />

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {fields.map((item, index) => {
          const categoryName = categoriesList.find(cat => cat.id === getValues(`variants.${index}.categoryId`))?.name;
          const isColorCategory = categoryName === 'Color'|| categoryName === 'اللون';
          const colorValue = colorValues[index] || getValues(`variants.${index}.languages.0.name`) || '#000000';

          return (
            <div key={item.id} className="p-4 rounded border border-muted relative space-y-4">

              <button
                type="button"
                onClick={() => remove(index)}
                className="absolute -top-3 -right-3 w-8 h-8 flex items-center justify-center rounded-full bg-red-500 hover:bg-red-600 text-white transition"
              >
                <Trash2 size={16} />
              </button>

              {/* Categories */}
              <div className="flex gap-2 flex-wrap mb-2">
                {categoriesList.map(cat => (
                  <button
                    type="button"
                    key={cat.id}
                    onClick={() => selectCategory(index, cat.id)}
                    className={`px-3 py-1 rounded border transition ${
                      getValues(`variants.${index}.categoryId`) === cat.id
                        ? 'bg-primary text-white border-primary'
                        : 'bg-white border-gray-300 text-gray-700'
                    }`}
                  >
                    {cat.name}
                  </button>
                ))}
              </div>
              {errors?.variants?.[index]?.categoryId && (
                <p className="text-red-500 text-sm">
                  {errors?.variants?.[index]?.categoryId?.message}
                </p>
              )}

              {/* Flex row: image + AR + EN + Price */}
              <div className="flex gap-4 items-start">
                {/* Image */}
                <div className="w-32">
                  <ImageUploader
                    label='optional'
                    images={images[`image_${index}`] ? [images[`image_${index}`]] : []}
                    setImages={(files) => files[0] && onImageChange(index, files[0])}
                    maxImages={1}
                  />
                </div>

                {/* Inputs */}
                <div className="flex-1 flex flex-col gap-2">
                  {isColorCategory ? (
                    <div className="flex flex-col gap-2">
                      <span>{t('select_color')}:</span>
                      <div className="border rounded p-2">
                        <HexColorPicker
                          color={colorValue}
                          onChange={(color) => {
                            setColorValues(prev => ({ ...prev, [index]: color }));
                            reset({
                              ...getValues(),
                              variants: getValues('variants').map((v, i) =>
                                i === index
                                  ? { ...v, languages: [{ languageCode: 'ar', name: color }, { languageCode: 'en', name: color }] }
                                  : v
                              )
                            });
                          }}
                        />
                        <div className="mt-2 text-center font-semibold">
                          {colorValue.toUpperCase()}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="flex gap-4">
                      <div className="flex-1">
                        <InputField
                          placeholder={`${t(`name_en`)}`}
                          register={register(`variants.${index}.languages.1.name`, { required: t('required_field') })}
                          error={errors?.variants?.[index]?.languages?.[1]?.name}
                        />
                        <input type="hidden" {...register(`variants.${index}.languages.1.languageCode`)} value="en" />
                      </div>
                      <div className="flex-1">
                        <InputField
                          placeholder={`${t(`name_ar`)}`}
                          register={register(`variants.${index}.languages.0.name`, { required: t('required_field') })}
                          error={errors?.variants?.[index]?.languages?.[0]?.name}
                        />
                        <input type="hidden" {...register(`variants.${index}.languages.0.languageCode`)} value="ar" />
                      </div>
                    </div>
                  )}

                  {/* Price full width */}
                  <div>
                    <InputField
                      placeholder={t('additional_price')}
                      type="number"
                      step='0.01'
                      register={register(`variants.${index}.additional_price`, { required: t('required_field'), valueAsNumber: true })}
                      error={errors?.variants?.[index]?.additional_price}
                    />
                  </div>
                </div>
              </div>
            </div>
          );
        })}

        {/* Add Variant Button */}
        <button
          type="button"
          onClick={() =>
            append({
              additional_price: 0,
              categoryId: undefined,
              languages: [
                { languageCode: 'ar', name: '' },
                { languageCode: 'en', name: '' }
              ]
            })
          }
          className="w-full py-3 bg-[#FFFFFF] flex justify-center items-center rounded border-[1.5px] border-dashed border-[#EBF3F0]"
        >
          <Plus size={20} />
        </button>
        {
          loading
          ?
          <LoadingButton/>
          :
          <SubmitButton label={t('create')} />
        }
        {
          backQuery!=="true"
          ?
          <Link to={navigatedLink}
          className={`bg-Black text-white px-6 py-2 h-[44px] text-xl  mt-2 w-full block text-center
          transition rounded-[8px] capitalize font-semibold hover:bg-opacity-85`}>
          {t('skip')}</Link>
          :
          <Link to={backLink}
          className={`bg-Black text-white px-6 py-2 h-[44px] text-xl  mt-2 w-full block text-center
          transition rounded-[8px] capitalize font-semibold hover:bg-opacity-85`}>
          {t('back')}</Link>
        }
      </form>
    </div>
  );
};